import { useState, type ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  BarChart3, Boxes, ChevronDown, Clock, FolderTree, LayoutDashboard, LogOut, Menu, Monitor,
  Building2, ClipboardList, Moon, Package, Receipt, ScrollText, Settings, ShoppingBag, ShoppingCart, Store, Sun, Truck, UserCog, Users, Wallet,
} from 'lucide-react'
import { PERMISSIONS, type Permission } from '@najah/core'
import { t } from '@najah/i18n'
import { Badge, Button, Logo, cn, useTheme } from '@najah/ui'
import { useAuth } from '../lib/auth'
import { SyncStatus } from './SyncStatus'

interface NavItem {
  to: string
  label: string
  icon: typeof LayoutDashboard
  permission?: Permission
  /** أقسام لم تُبنَ بعد — تظهر معطّلة بدل أن تقود إلى صفحة فارغة */
  soon?: boolean
}

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'العمليات',
    items: [
      { to: '/', label: t.nav.dashboard, icon: LayoutDashboard },
      { to: '/pos', label: t.nav.pos, icon: ShoppingCart, permission: PERMISSIONS.SALE_CREATE },
      { to: '/sales', label: t.nav.sales, icon: Receipt, permission: PERMISSIONS.SALE_VIEW },
      { to: '/shifts', label: 'الورديات والصندوق', icon: Clock, permission: PERMISSIONS.SALE_CREATE },
      { to: '/orders', label: t.nav.orders, icon: Truck, permission: PERMISSIONS.ORDER_VIEW },
    ],
  },
  {
    group: 'المخزون',
    items: [
      { to: '/products', label: t.nav.products, icon: Package, permission: PERMISSIONS.PRODUCT_VIEW },
      { to: '/categories', label: t.nav.categories, icon: FolderTree, permission: PERMISSIONS.PRODUCT_VIEW },
      { to: '/inventory', label: t.nav.inventory, icon: Boxes, permission: PERMISSIONS.STOCK_VIEW },
      { to: '/inventory/movements', label: t.nav.stockMovements, icon: ScrollText, permission: PERMISSIONS.STOCK_VIEW },
      { to: '/stock-takes', label: t.nav.stockTakes, icon: ClipboardList, permission: PERMISSIONS.STOCK_VIEW },
      { to: '/purchases', label: t.nav.purchases, icon: ShoppingBag, permission: PERMISSIONS.PURCHASE_VIEW },
      { to: '/suppliers', label: t.nav.suppliers, icon: Building2, permission: PERMISSIONS.PURCHASE_VIEW },
      { to: '/expenses', label: t.nav.expenses, icon: Wallet, permission: PERMISSIONS.PURCHASE_VIEW },
    ],
  },
  {
    group: 'الإدارة',
    items: [
      { to: '/customers', label: t.nav.customers, icon: Users, permission: PERMISSIONS.CUSTOMER_VIEW },
      { to: '/reports', label: t.nav.reports, icon: BarChart3, permission: PERMISSIONS.REPORT_VIEW },
      { to: '/users', label: t.nav.users, icon: UserCog, permission: PERMISSIONS.USER_MANAGE },
      { to: '/branches', label: t.nav.branches, icon: Store, permission: PERMISSIONS.SETTINGS_MANAGE },
      { to: '/settings', label: t.nav.settings, icon: Settings, permission: PERMISSIONS.SETTINGS_MANAGE },
    ],
  },
]

function ThemeToggle() {
  const { mode, setMode } = useTheme()
  const next = { light: 'dark', dark: 'system', system: 'light' } as const
  const Icon = mode === 'light' ? Sun : mode === 'dark' ? Moon : Monitor
  const labels = { light: t.theme.light, dark: t.theme.dark, system: t.theme.system }

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => setMode(next[mode])}
      title={`${t.theme.toggle}: ${labels[mode]}`}
      aria-label={`${t.theme.toggle}: ${labels[mode]}`}
    >
      <Icon className="size-4" />
    </Button>
  )
}

function UserMenu() {
  const { user, logout } = useAuth()
  const [open, setOpen] = useState(false)
  if (!user) return null

  const initials = user.fullName.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('')

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-lg py-1.5 pe-2 ps-1.5 transition-colors hover:bg-surface-hover"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="grid size-8 place-items-center rounded-full bg-primary-subtle text-xs font-semibold text-primary-subtle-fg">
          {initials}
        </span>
        <span className="hidden text-start sm:block">
          <span className="block text-sm font-medium leading-tight text-fg">{user.fullName}</span>
          <span className="block text-[11px] leading-tight text-fg-subtle">{user.roleNameAr}</span>
        </span>
        <ChevronDown className={cn('size-4 text-fg-subtle transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <>
          {/* طبقة تلتقط النقر خارج القائمة لإغلاقها */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            role="menu"
            className="absolute end-0 top-full z-50 mt-1.5 w-56 animate-[scale-in_0.16s_var(--ease-out-quint)] origin-top rounded-xl border border-border bg-surface p-1.5 shadow-lg"
          >
            <div className="border-b border-border px-3 py-2">
              <p className="text-sm font-medium text-fg">{user.fullName}</p>
              <p dir="ltr" className="text-start text-xs text-fg-subtle">@{user.username}</p>
              {user.storeName && <p className="mt-1 text-xs text-fg-muted">{user.storeName}</p>}
            </div>
            <button
              role="menuitem"
              onClick={() => void logout()}
              className="mt-1 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-danger transition-colors hover:bg-danger-subtle"
            >
              <LogOut className="size-4 icon-flip" />
              {t.auth.logout}
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useAuth()
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  return (
    <nav className="flex h-full flex-col gap-6 overflow-y-auto p-4">
      {NAV.map((section) => {
        const items = section.items.filter((i) => !i.permission || can(i.permission))
        if (!items.length) return null

        return (
          <div key={section.group}>
            <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">
              {section.group}
            </p>
            <ul className="space-y-0.5">
              {items.map((item) => {
                const active = pathname === item.to
                const Icon = item.icon

                if (item.soon) {
                  return (
                    <li key={item.to}>
                      <span
                        className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm text-fg-subtle opacity-60"
                        title="قيد التطوير"
                      >
                        <Icon className="size-[18px] shrink-0" />
                        <span className="flex-1">{item.label}</span>
                        <Badge tone="neutral" className="text-[10px]">قريباً</Badge>
                      </span>
                    </li>
                  )
                }

                return (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      onClick={onNavigate}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                        active
                          ? 'bg-primary-subtle font-medium text-primary-subtle-fg'
                          : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                      )}
                    >
                      <Icon className="size-[18px] shrink-0" />
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </nav>
  )
}

export function AppShell({ children }: { children: ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="min-h-dvh bg-bg">
      {/* الشريط الجانبي ثابت على بداية السطر — أي اليمين في RTL */}
      <aside className="fixed inset-y-0 start-0 z-30 hidden w-64 border-e border-border bg-surface lg:block">
        <div className="flex h-16 items-center border-b border-border px-5">
          <Logo />
        </div>
        <div className="h-[calc(100dvh-4rem)]">
          <Sidebar />
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 animate-[fade-in_0.18s_ease] bg-[var(--overlay)]"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute inset-y-0 start-0 w-72 animate-[slide-up_0.22s_var(--ease-out-quint)] border-e border-border bg-surface">
            <div className="flex h-16 items-center border-b border-border px-5">
              <Logo />
            </div>
            <div className="h-[calc(100dvh-4rem)]">
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </div>
          </aside>
        </div>
      )}

      <div className="lg:ps-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-surface/85 px-4 backdrop-blur-md sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="فتح القائمة"
          >
            <Menu className="size-5" />
          </Button>

          <div className="flex-1" />
          <SyncStatus />
          <ThemeToggle />
          <div className="mx-1 h-6 w-px bg-border" aria-hidden />
          <UserMenu />
        </header>

        <main className="px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  )
}
